import { INullable, TObject } from 'utils/types';

import { RECON_MULTI_OPERATIONS } from './constant';
import {
  IFolderFile,
  IReconciliationFile,
  IReconciliationFolder,
  IReconciliationFolderData,
  IReconciliationFolderForm,
  IReconciliationFolderMapping,
  IReconciliationFolderMappingData,
  IReconciliationFolderMappingValues,
  IReconciliationFolderMappingValuesData,
  IReconciliationResults,
  IReconFilter,
  IReconInfo,
  IStartReconciliationForm,
  IStats,
  ITerminalLink,
} from './types';

const normalizeReconFilter = (data: TObject): IReconFilter => {
  const operation = (data?.operation as string) || null;
  const rawValue = (data?.value as string) || null;
  const isMulti = !!operation && RECON_MULTI_OPERATIONS.includes(operation);

  return {
    fieldName: (data?.fieldName as string) || null,
    operation,
    value: isMulti && rawValue ? rawValue.split(',') : rawValue,
  };
};

/** Keep only filled rows and comma-join multi-value (IN / NOT IN) values for the wire. */
const prepareReconFilters = (filters?: IReconFilter[]): IReconFilter[] => {
  return (
    filters
      ?.filter((filter) => !!filter.fieldName)
      .map((filter) => ({
        ...filter,
        value: Array.isArray(filter.value)
          ? filter.value.filter((item) => item != null && item !== '').join(',')
          : filter.value,
      })) || []
  );
};

export const normalizeReconciliationFolderPayload = (
  payload: IReconciliationFolderForm,
): IReconciliationFolderForm => {
  return {
    ...payload,
    filters: prepareReconFilters(payload.filters),
  };
};

export const normalizeStartReconciliationPayload = (
  payload: IStartReconciliationForm,
): IStartReconciliationForm => {
  return {
    ...payload,
    dynamicFilters:
      payload.mode === 'FILTER' ? prepareReconFilters(payload.dynamicFilters) : [],
  };
};

const normalizeTerminalLink = (data: TObject): ITerminalLink => {
  return {
    description: (data?.description as string) || null,
    id: (data?.id as number) || null,
  };
};

const normalizeReconInfo = (data: TObject): INullable<IReconInfo> => {
  if (!data?.startDate || !data?.endDate) {
    return null;
  }

  return {
    endDate: data.endDate as string,
    startDate: data.startDate as string,
  };
};

const normalizeFile = (data: TObject): IFolderFile => {
  return {
    id: (data?.id as number) || 0,
    name: (data?.name as string) || null,
    reconInfo: normalizeReconInfo(data?.reconInfo as TObject),
  };
};

const normalizeStats = (data: TObject): IStats => {
  return {
    cashierMissed: (data?.cashierMissed as number) || 0,
    filesCount: (data?.filesCount as number) || 0,
    matchCount: (data?.matchCount as number) || 0,
    mismatchByAmount: (data?.mismatchByAmount as number) || 0,
    mismatchByCurrency: (data?.mismatchByCurrency as number) || 0,
    mismatchByStatus: (data?.mismatchByStatus as number) || 0,
    pspMissed: (data?.pspMissed as number) || 0,
    resolvedCount: (data?.resolvedCount as number) || 0,
    totalMismatch: (data?.totalMismatch as number) || 0,
    totalTransactions: (data?.totalTransactions as number) || 0,
  };
};

export const normalizeReconciliationFolder = (
  data: IReconciliationFolderData,
): IReconciliationFolder => {
  return {
    createdDate: (data?.createdDate as string) || null,
    files: data?.files?.map(normalizeFile) || [],
    filesCount: (data?.filesCount as number) || 0,
    filters: data?.filters?.map(normalizeReconFilter) || [],
    id: (data?.id as number) || 0,
    merchant: {
      id: (data?.merchant?.id as number) || null,
      name: (data?.merchant?.name as string) || null,
    },
    name: (data?.name as string) || null,
    stats: normalizeStats(data?.stats),
    terminalLinks: data?.terminalLinks?.map(normalizeTerminalLink) || [],
    terminalLinksCount: (data?.terminalLinksCount as number) || null,
  };
};

export const normalizeReconciliationFile = (data: TObject): IReconciliationFile => {
  return {
    ...normalizeStats(data),
    name: (data?.name as string) || null,
    reconInfo: normalizeReconInfo(data?.reconInfo as TObject),
  };
};

export const normalizeReconciliationResults = (data: TObject): IReconciliationResults => {
  return {
    amount: (data?.amount as number) || null,
    comment: (data?.comment as string) || null,
    created: (data?.created as string) || null,
    csvAmount: (data?.csvAmount as number) || null,
    csvCreated: (data?.csvCreated as string) || null,
    csvCurrency: (data?.csvCurrency as string) || null,
    csvCustomerAccessToken: (data?.csvCustomerAccessToken as string) || null,
    csvExecutionId: (data?.csvExecutionId as string) || null,
    csvMerchantTransactionId: (data?.csvMerchantTransactionId as string) || null,
    csvOriginState: (data?.csvOriginState as string) || null,
    csvOriginType: (data?.csvOriginType as string) || null,
    csvPaymentMethod: (data?.csvPaymentMethod as string) || null,
    csvPspExecutionId: (data?.csvPspExecutionId as string) || null,
    csvState: (data?.csvState as string) || null,
    csvTransactionId: (data?.csvTransactionId as string) || null,
    csvType: (data?.csvType as string) || null,
    currency: (data?.currency as string) || null,
    customerAccessToken: (data?.customerAccessToken as string) || null,
    diffType: (data?.diffType as string) || null,
    executionId: (data?.executionId as string) || null,
    merchantTransactionId: (data?.merchantTransactionId as string) || null,
    mismatchDescription: (data?.mismatchDescription as string) || null,
    paymentMethod: (data?.paymentMethod as string) || null,
    pspExecutionId: (data?.pspExecutionId as string) || null,
    state: (data?.state as string) || null,
    transactionId: (data?.transactionId as string) || null,
    type: (data?.type as string) || null,
  };
};

export const normalizeReconciliationFolderMapping = (
  data: IReconciliationFolderMappingData,
): IReconciliationFolderMapping => {
  return {
    result: data?.result.map((item) => ({
      cashierFieldName: (item.cashierFieldName as string) || null,
      customFieldName: (item.customFieldName as string) || null,
      id: (item.id as number) || null,
      name: (item.name as string) || null,
    })),
  };
};

export const normalizeReconciliationFolderMappingValues = (
  data: IReconciliationFolderMappingValuesData,
): IReconciliationFolderMappingValues => {
  return {
    result: data?.result.map((item) => ({
      cashierFieldValue: (item.cashierFieldValue as string) || null,
      csvFieldValue: item?.csvFieldValue?.map((item) => ({ value: item.value })) || null,
      fieldName: (item.fieldName as string) || null,
      id: (item.id as number) || null,
    })),
  };
};
